import { SCRATCH_TYPE, SC_CONFIG } from 'scratchcard-js';
import type { ScratchcardGameSettings } from '@kanek/shared-data-access';

/**
 * ScratchcardConfigMapper
 * 
 * Maps our ScratchcardGameSettings to scratchcard-js SC_CONFIG format.
 * Handles defaults and type conversions.
 */
export class ScratchcardConfigMapper {
  
  /**
   * Convert our settings to scratchcard-js config
   */
  static toScratchcardConfig(settings: ScratchcardGameSettings): SC_CONFIG {
    // Use defaults if values are empty strings or undefined
    const imageForwardSrc = settings.imageForwardSrc?.trim() || this.getDefaultForwardImage();
    const imageBackgroundSrc = settings.imageBackgroundSrc?.trim() || this.getDefaultBackgroundImage();
    const brushSrc = settings.brushSrc?.trim() || this.getDefaultBrushImage();
    
    // Generate HTML background with proper dimensions
    const containerWidth = settings.containerWidth || 400;
    const containerHeight = settings.containerHeight || 300;
    const backgroundColor = settings.htmlBackground?.trim() || '#22C55E';
    const htmlBackground = this.generateHtmlBackground(containerWidth, containerHeight, backgroundColor);

    const config: SC_CONFIG = {
      scratchType: this.mapScratchType(settings.scratchType),
      containerWidth,
      containerHeight,
      imageForwardSrc,
      imageBackgroundSrc,
      htmlBackground,
      clearZoneRadius: settings.clearZoneRadius || 30,
      nPoints: settings.nPoints || 100,
      pointSize: settings.pointSize || 10,
      percentToFinish: settings.percentToFinish || 70,
      brushSrc,
      enabledPercentUpdate: true,
      callback: () => {
        // No-op callback for preview mode
        // Prevents default alert behavior
        console.log('🎉 Scratchcard completed (preview mode)');
      },
    };

    return config;
  }

  /**
   * Map our scratch type string to scratchcard-js SCRATCH_TYPE enum
   * Public so it can be used by grid mode
   */
  static mapScratchType(type: string): SCRATCH_TYPE {
    const typeMap: Record<string, SCRATCH_TYPE> = {
      'brush': SCRATCH_TYPE.BRUSH,
      'spray': SCRATCH_TYPE.SPRAY,
      'circle': SCRATCH_TYPE.CIRCLE,
      'line': SCRATCH_TYPE.LINE,
    };

    return typeMap[type] || SCRATCH_TYPE.BRUSH;
  }

  /**
   * Default scratch layer image (cover image)
   * Must be an actual asset file path - base64 doesn't work reliably
   */
  private static getDefaultForwardImage(): string {
    return 'assets/scratchcard/kanek-scratch-cover-clean.svg';
  }

  /**
   * Default background image (revealed layer)
   * Uses transparent 1x1 SVG so htmlBackground shows through
   */
  private static getDefaultBackgroundImage(): string {
    // Transparent 1x1 pixel SVG
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+';
  }

  /**
   * Default brush image for scratch cursor
   * REQUIRED - empty string causes errors
   */
  private static getDefaultBrushImage(): string {
    return '/assets/logos/kanek/kanek-logo-black.png';
  }

  /**
   * Generate HTML background for revealed content
   * Matches the old game-preview implementation exactly
   */
  private static generateHtmlBackground(width: number, height: number, backgroundColor: string): string {
    return `<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: ${width}px; height: ${height}px; background: ${backgroundColor}; color: white; font-family: Arial, sans-serif; box-sizing: border-box; user-select: none;"><div style="font-size: 32px; margin-bottom: 10px; pointer-events: none;">🎁</div><div style="font-size: 18px; font-weight: bold; text-align: center; padding: 0 20px; margin-bottom: 8px; pointer-events: none; text-shadow: 1px 1px 2px rgba(0,0,0,0.7);">You Won!</div><div style="font-size: 14px; text-align: center; padding: 0 20px; opacity: 0.9; pointer-events: none; text-shadow: 1px 1px 2px rgba(0,0,0,0.7);">Prize Preview</div></div>`;
  }
}
